/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
/**
 * SITACSymbolSelector.tsx
 * 
 * POSIZIONE: src/components/SITAC/SITACSymbolSelector.tsx
 * 
 * VERSIONE CORRETTA con caricamento async del catalogo
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  loadCatalogo,
  getSITACSymbols, 
  getSymbolPath,
  searchSITACSymbols,
  getCategoryColor,
  getCategoryLabel,
  SITAC_CATEGORIES,
  type SITACCategory,
  type SITACSymbol 
} from '@/config/sitac';

export interface SITACSymbolWithCategory extends SITACSymbol {
  categoria: SITACCategory;
}

interface SITACSymbolSelectorProps {
  onSymbolSelect: (symbol: SITACSymbolWithCategory) => void;
  initialCategory?: SITACCategory;
  compact?: boolean;
  showCount?: boolean;
}

export const SITACSymbolSelector: React.FC<SITACSymbolSelectorProps> = ({
  onSymbolSelect,
  initialCategory = 'zona_intervento',
  compact = false,
  showCount = true
}) => {
  const [activeCategory, setActiveCategory] = useState<SITACCategory>(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredSymbol, setHoveredSymbol] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carica il catalogo all'avvio
  useEffect(() => {
    console.log('🎯 SITACSymbolSelector: Componente montato!');
    
    const loadData = async () => {
      try {
        console.log('🔄 SITACSymbolSelector: Inizio caricamento catalogo...');
        await loadCatalogo();
        console.log('✅ SITACSymbolSelector: Catalogo caricato con successo!');
        setLoading(false);
      } catch (err) {
        console.error('❌ SITACSymbolSelector: Errore caricamento:', err);
        setError('Errore caricamento catalogo SITAC');
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const categorySymbols = useMemo(() => {
    if (loading) return [];
    return getSITACSymbols(activeCategory);
  }, [activeCategory, loading]);

  const filteredSymbols = useMemo(() => {
    if (loading) return [];
    if (!searchQuery.trim()) {
      return categorySymbols.map(s => ({ ...s, categoria: activeCategory }));
    }
    return searchSITACSymbols(searchQuery);
  }, [categorySymbols, searchQuery, activeCategory, loading]);

  const handleCategoryChange = (category: SITACCategory) => {
    setActiveCategory(category);
    setSearchQuery('');
  };

  const handleSymbolClick = (symbol: SITACSymbol) => {
    onSymbolSelect({
      ...symbol,
      categoria: searchQuery ? 
        (filteredSymbols.find(s => s.nome === symbol.nome) as SITACSymbolWithCategory)?.categoria || activeCategory 
        : activeCategory
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.loadingSpinner}>⏳</div>
          <p>Caricamento simboli SITAC...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <p>❌ {error}</p>
          <small>Verifica che catalogo.json sia in public/assets/sitac/</small>
        </div>
      </div>
    );
  }

  return (
    <div className={`sitac-symbol-selector ${compact ? 'compact' : ''}`} style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Simboli SITAC</h3>
        <div style={styles.searchBox}>
          <input
            type="text"
            placeholder="Cerca simbolo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              style={styles.clearButton}
              title="Cancella ricerca"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div style={styles.categoryPills}>
        {(Object.keys(SITAC_CATEGORIES) as SITACCategory[]).map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            style={{
              ...styles.categoryPill,
              ...(activeCategory === category ? styles.categoryPillActive : {}),
              backgroundColor: activeCategory === category ? getCategoryColor(category) : '#fff',
              color: activeCategory === category ? '#fff' : '#666',
              borderColor: activeCategory === category ? getCategoryColor(category) : '#ddd'
            }}
            title={getCategoryLabel(category)}
          >
            <span style={styles.categoryLabel}>
              {compact 
                ? getCategoryLabel(category).split(' ')[0] 
                : getCategoryLabel(category)
              }
            </span>
            {showCount && (
              <span style={styles.categoryCount}>
                {getSITACSymbols(category).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={styles.symbolsGrid}>
        {filteredSymbols.length === 0 ? (
          <div style={styles.emptyState}>
            {searchQuery ? (
              <>
                <p>Nessun simbolo trovato per "{searchQuery}"</p>
                <button 
                  style={styles.clearSearchButton}
                  onClick={() => setSearchQuery('')}
                >
                  Cancella ricerca
                </button>
              </>
            ) : (
              <p>Nessun simbolo disponibile</p>
            )}
          </div>
        ) : (
          filteredSymbols.map((symbol) => {
            const cat = 'categoria' in symbol ? symbol.categoria : activeCategory;
            return (
              <div
                key={`${cat}-${symbol.nome}`}
                style={styles.symbolCard}
                onClick={() => handleSymbolClick(symbol)}
                onMouseEnter={() => setHoveredSymbol(symbol.nome)}
                onMouseLeave={() => setHoveredSymbol(null)}
              >
                <div style={styles.symbolImageWrapper}>
                  <img
                    src={getSymbolPath(cat, symbol.file)}
                    alt={symbol.descrizione}
                    style={styles.symbolImage}
                  />
                </div>
                
                {hoveredSymbol === symbol.nome && (
                  <div style={styles.tooltip}>
                    {symbol.descrizione}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div style={styles.footer}>
        <small style={styles.footerInfo}>
          {filteredSymbols.length} {filteredSymbols.length === 1 ? 'simbolo' : 'simboli'}
          {searchQuery && ' trovati'}
        </small>
        <small style={styles.footerCategory}>
          {getCategoryLabel(activeCategory)}
        </small>
      </div>
    </div>
  );
};

// Stili rimangono uguali...
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#666'
  },
  loadingSpinner: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  error: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '20px',
    textAlign: 'center',
    color: '#d32f2f'
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid #e0e0e0',
    background: 'linear-gradient(to bottom, #ffffff, #f8f8f8)'
  },
  title: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#333'
  },
  searchBox: {
    position: 'relative'
  },
  searchInput: {
    width: '100%',
    padding: '10px 36px 10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  clearButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: '4px 8px',
    cursor: 'pointer',
    color: '#999',
    fontSize: '16px',
    borderRadius: '4px'
  },
  categoryPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    overflowX: 'auto'
  },
  categoryPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    border: '2px solid',
    borderRadius: '16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s'
  },
  categoryPillActive: {
    fontWeight: 600
  },
  categoryLabel: {},
  categoryCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600
  },
  symbolsGrid: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
    gap: '12px',
    alignContent: 'start'
  },
  symbolCard: {
    position: 'relative',
    aspectRatio: '1',
    border: '2px solid #e5e5e5',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  },
  symbolImageWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  symbolImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    userSelect: 'none',
    pointerEvents: 'none'
  },
  tooltip: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    pointerEvents: 'none'
  },
  emptyState: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#999'
  },
  clearSearchButton: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    marginTop: '12px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  footerInfo: {
    fontSize: '12px',
    color: '#666'
  },
  footerCategory: {
    fontSize: '12px',
    color: '#666',
    fontWeight: 500
  }
};

export default SITACSymbolSelector;
